import React, { Component } from 'react'
import PropTypes from 'prop-types'
import actions from 'redux/actions'
import { connect } from 'redaction'
import moment from 'moment'
import { formatPrice, constants, compose, sentry } from 'common/helpers'
import { FormattedHTMLMessage, injectIntl } from 'sb-react-intl'

import injectGlobals from 'decorators/injectGlobals'

import ButtonsInRow from 'components/_new/buttons/ButtonsInRow/ButtonsInRow'
import CommonButton from 'components/buttons/CommonButton/CommonButton'
import Bill from 'components/payment/Bill/Bill'

import Title from '../Title/Title'

import messages from './messages'
import s from './SelectedPlanSummary.scss'


class SelectedPlanSummary extends Component {

  static propTypes = {
    showLoader: PropTypes.func,
    hideLoader: PropTypes.func,
    placement: PropTypes.string,
    onClose: PropTypes.func,
    goToStep: PropTypes.func,
    intl: PropTypes.object,
    selectedPlan: PropTypes.object,
    costs: PropTypes.shape({
      balance: PropTypes.number,
      tax: PropTypes.number,
      inquirePlan: PropTypes.object,
    }),
    upgradeActivationMonth: PropTypes.string,
    nextChargeMonth: PropTypes.string,
    isFetching: PropTypes.bool,
  }

  static defaultProps = {
    isFetching: false,
    showLoader: () => {},
    hideLoader: () => {},
    selectedPlan: {},
  }

  componentDidMount() {
    const { hideLoader, showLoader, selectedPlan: { name } } = this.props

    if (!Boolean(name)) {
      sentry.captureException(`SelectedPlanSummary without selectedPlan`)
      this.goPrevStep()
    }
    else {
      showLoader()

      actions.models.subscription.fetchPlanCosts(name)
        .finally(hideLoader)
        .catch(() => {
          this.goPrevStep()
        })
    }
  }

  goPrevStep = () => {
    const { goToStep } = this.props

    goToStep(constants.upgradeSubscriptionFlowSteps.selectPlan)
  }

  goNextStep = () => {
    const { showLoader, hideLoader, selectedPlan: { name }, goToStep, placement, onClose } = this.props

    showLoader()

    actions.models.subscription.changePlan(name, placement)
      .then(
        (error) => {
          hideLoader()

          if (error instanceof Error) {
            if (typeof onClose === 'function') {
              onClose()
            }
          }
          else {
            goToStep(constants.upgradeSubscriptionFlowSteps.upgradeSuccess)
          }
        },
        (err) => {
          console.error(err)
          if (typeof onClose === 'function') {
            onClose()
          }
        }
      )
  }

  getSummaryItems = () => {
    const { intl, selectedPlan: { scentCount, monthCount }, costs: { balance, tax, inquirePlan = {} } } = this.props
    const { totalPrice } = inquirePlan

    let items = [
      {
        label: {
          ...messages.planName,
          values: {
            scentCount,
            monthCount,
          },
        },
        value: formatPrice(totalPrice, true),
      },
      {
        label: messages.currBalance,
        value: formatPrice(-balance, true),
        tooltip: messages.balanceTooltipText,
      },
      {
        label: messages.tax,
        value: formatPrice(tax, true),
      },
      {
        label: messages.shipping,
        value: intl.formatMessage(messages.free),
      },
    ]

    return items
  }

  render() {
    const {
      costs: { priceWithCredits },
      upgradeActivationMonth, nextChargeMonth, isActivationDateInSummaryEnabled,
      isFetching,
    } = this.props

    if (isFetching) {
      return null
    }

    const summaryItems         = this.getSummaryItems()
    const isChargeInfoVisible  = (
      isActivationDateInSummaryEnabled
      && (upgradeActivationMonth || nextChargeMonth)
    )

    return (
      <div className={s.selectedPlanSummary}>
        <Title message={messages.title} />
        <Bill
          items={summaryItems}
          total={priceWithCredits}
        />
        {
          isChargeInfoVisible && (
            <div className={s.note}>
              {
                Boolean(upgradeActivationMonth) && (
                  <FormattedHTMLMessage tag="p" values={{ upgradeActivationMonth }} {...messages.estimateNote} />
                )
              }
              {
                Boolean(nextChargeMonth) && (
                  <FormattedHTMLMessage tag="p" values={{ nextChargeMonth }} {...messages.nextChargeNote} />
                )
              }
            </div>
          )
        }
        <ButtonsInRow className={s.buttons}>
          <CommonButton
            className={s.button}
            emptyGray
            back
            onClick={this.goPrevStep}
          />
          <CommonButton
            className={s.button}
            brand
            confirm
            onClick={this.goNextStep}
            dataTestId={constants.testIds.upgrade.confirmButton}
          />
        </ButtonsInRow>
        <FormattedHTMLMessage className={s.attention} tag="div" {...messages.attention} />
      </div>
    )
  }
}


export default compose(
  injectGlobals(({ getFeatureToggle }) => ({
    isActivationDateInSummaryEnabled: getFeatureToggle(constants.features.upgradeActivationDateInSummary),
  })),
  connect((state, props) => {
    const { models: { subscription: { planCosts } } } = state
    const { isActivationDateInSummaryEnabled } = props
    const { upgradeActivationDate, chargeCategory, nextBillingYearMonth } = planCosts

    const data = {
      costs: planCosts,
      selectedPlan: actions.models.upgradeSubscriptionFlow.getSelectedPlan(state) || {},
      isActivationDateInSummaryEnabled,
    }

    const dateFormat = 'MMMM'

    if (upgradeActivationDate) {
      data.upgradeActivationMonth = moment(upgradeActivationDate).format(dateFormat)
    }

    if (nextBillingYearMonth && chargeCategory === constants.upgradeSubscriptionTypes.nonCharge) {
      data.nextChargeMonth = moment(nextBillingYearMonth).format(dateFormat)
    }

    return data
  }),
  injectIntl,
)(SelectedPlanSummary)
